import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import {
  citiesData,
  citiesDataError,
  citiesDataLoading,
} from '../../../../../redux/features/dashboard/dashboardSelectors';
import {
  useAddStreetMutation,
  useDeleteStreetMutation,
  useGetStreetQuery,
  useUpdateStreetMutation,
} from '../../../../../redux/services/data/streetsApi';

import { FormTemplate } from '../../FormTemplate';
import { InputsTemplate } from '../../../inputs/InputsTemplate';
import { Loader } from '../../../../ui/Loader';
import { DeleteDialogButton } from '../../../DeleteDialogButton';

import {
  initialValues,
  mock,
  validationSchemaStreet,
} from '../../../../../lib/mocks/add-street-mock';
import {
  getDefaultValues,
  handleResponseError,
} from '../../../../../utils/data';
import { closeDialog, showError } from '../../../../../utils/ui';
import {
  errorMessages,
  selectsLocationNames,
} from '../../../../../data/constants';

export const AddStreetForm = ({
  formProps = {},
  name = {},
  isEdit = false,
  handleClose = () => {},
  handleResetInput = () => {},
}) => {
  const [error, setError] = useState('');

  const userToken = useSelector(selectUserToken);
  const cities = useSelector(citiesData);
  const citiesLoading = useSelector(citiesDataLoading);
  const citiesError = useSelector(citiesDataError);

  // Memoized values from parent form
  const streetId = useMemo(
    () => formProps?.values?.[name],
    [formProps?.values, name]
  );
  const selectedCityId = useMemo(
    () => formProps?.values?.[selectsLocationNames.city],
    [formProps?.values]
  );
  const {
    data: street,
    isFetching: isStreetLoading,
    error: streetError,
  } = useGetStreetQuery(
    { token: userToken, id: streetId },
    { skip: !isEdit || !streetId || !userToken }
  );

  const [addStreet, { isLoading: isAddingLoading, error: addingError }] =
    useAddStreetMutation();
  const [editStreet, { isLoading: isEditingLoading, error: editingError }] =
    useUpdateStreetMutation();
  const [deleteStreet, { isLoading: isDeletingLoading, error: deletingError }] =
    useDeleteStreetMutation();

  // Combined loading state
  const isLoading =
    citiesLoading ||
    (isEdit && isStreetLoading) ||
    isAddingLoading ||
    isEditingLoading ||
    isDeletingLoading;

  // Find selected city object from cities array
  const selectedCity = useMemo(() => {
    if (!selectedCityId || !cities?.length) return null;
    return cities.find(
      c =>
        c.id.toString() === selectedCityId.toString() ||
        c.value.toString() === selectedCityId.toString()
    );
  }, [selectedCityId, cities]);

  // Prepare mock config with cities data injected
  const mockWithData = useMemo(() => {
    if (!cities) return mock;
    return mock.map(item => {
      if (item.name === 'city_id') {
        return { ...item, selectData: cities };
      }
      return item;
    });
  }, [cities]);

  // Prepare form initial values
  const formInitialValues = useMemo(() => {
    if (isEdit && street) {
      // Edit mode: use fetched street data
      return {
        name: street.name || '',
        city_id: street.city_id || selectedCityId || '',
      };
    }
    // Add mode: use selected city from parent form
    return {
      ...initialValues,
      city_id: selectedCityId || '',
    };
  }, [isEdit, street, selectedCityId]);

  // Prepare mock config with default values for form fields
  const mockWithValues = useMemo(() => {
    if (isEdit && street) {
      // For edit mode, set defaultValue for city select
      return getDefaultValues(
        {
          city_id: selectedCity || { value: street.city_id, title: 'Unknown' },
        },
        mockWithData
      );
    } else if (!isEdit && selectedCity) {
      // For add mode, set defaultValue for pre-selected city
      return getDefaultValues({ city_id: selectedCity }, mockWithData);
    }
    return mockWithData;
  }, [isEdit, street, selectedCity, mockWithData]);

  // Handle delete
  const handleDelete = async setIsDeleteClick => {
    setIsDeleteClick(false);
    setError('');

    const response = await deleteStreet({
      token: userToken,
      id: streetId,
    });

    const errorStatus = handleResponseError(
      response?.error,
      deletingError,
      () => closeDialog({ handleClose })
    );

    if (!errorStatus) {
      handleResetInput();
    }

    setError(errorStatus);
  };

  // Handle submit
  const handleSubmit = async values => {
    setError(null);

    let response;
    let errorStatus;

    if (isEdit) {
      response = await editStreet({
        token: userToken,
        id: streetId,
        data: values,
      });

      errorStatus = handleResponseError(response?.error, editingError, () =>
        closeDialog({ handleClose })
      );
    } else {
      response = await addStreet({ token: userToken, data: values });

      errorStatus = handleResponseError(response?.error, addingError, () =>
        closeDialog({ handleClose })
      );
    }

    setError(errorStatus);
  };

  // Show errors
  useEffect(() => {
    if (citiesError || streetError) {
      setError(errorMessages.wentWrong);
    }
  }, [citiesError, streetError]);

  useEffect(() => {
    showError(error);
  }, [error]);

  return (
    <div className="relative flex flex-col gap-4 w-[260px] sm:w-[420px] exclude-click">
      {!isLoading && (
        <FormTemplate
          initialValues={formInitialValues}
          validationSchema={Yup.object(validationSchemaStreet)}
          handleSubmitForm={handleSubmit}
          buttonText={'Save'}
          bgColor={'bg-purpleColor border-purpleColor'}
        >
          {formProps => (
            <>
              <InputsTemplate
                formProps={formProps}
                inputsList={mockWithValues}
              />

              {isEdit && (
                <DeleteDialogButton
                  isDeletingLoading={isDeletingLoading}
                  handleDelete={handleDelete}
                />
              )}
            </>
          )}
        </FormTemplate>
      )}

      {isLoading && (
        <div className="flex items-center justify-center w-full h-[152px]">
          <Loader />
        </div>
      )}
    </div>
  );
};
